import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Textarea,
} from '@chakra-ui/react';
import React from 'react';

import { useI18n } from 'lib/i18n/context';
import IconSvg from 'ui/shared/IconSvg';

import { ADDRESS_CONTRACT_KEYS } from '../../../i18n';
import { useContractMethodFieldStyles } from './contractMethodFieldStyles';
import parseBatchAddresses from './parseBatchAddresses';

interface Props {
  onAdd: (addresses: Array<string>) => void;
  onClose: () => void;
}

const ContractMethodAddressArrayBatchPaste = ({ onAdd, onClose }: Props) => {
  const { t } = useI18n();
  const [ value, setValue ] = React.useState('');
  const styles = useContractMethodFieldStyles();

  const panelBg = styles.inputBg;
  const panelBorder = styles.inputBorder;
  const textareaBg = styles.nestedInputBg;
  const textareaBorder = styles.inputBorder;
  const titleColor = styles.inputText;
  const textColor = styles.inputText;
  const placeholderColor = styles.inputPlaceholder;
  const closeIconColor = styles.labelColor;
  const errorBg = 'rgba(254, 96, 72, 0.12)';
  const errorBorder = '#FE6048';
  const errorTextColor = '#FE6048';

  const { addresses, invalidAddresses } = React.useMemo(() => parseBatchAddresses(value), [ value ]);
  const canAdd = value.trim().length > 0 && invalidAddresses.length === 0 && addresses.length > 0;

  const handleAdd = React.useCallback(() => {
    if (!canAdd) {
      return;
    }

    onAdd(addresses);
    setValue('');
  }, [ addresses, canAdd, onAdd ]);

  const handleClose = React.useCallback(() => {
    setValue('');
    onClose();
  }, [ onClose ]);

  const handleValueChange = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
  }, []);

  const textareaStateProps = {
    bgColor: textareaBg,
    borderColor: textareaBorder,
    boxShadow: 'none',
  } as const;

  return (
    <Box
      bg={ panelBg }
      border="1px solid"
      borderColor={ panelBorder }
      borderRadius="4px"
      p={ 4 }
      w="100%"
      overflow="hidden"
    >
      <Flex flexDir="column" gap={ 3 }>
        <Flex flexDir="column" gap={ 2 }>
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontSize="12px" fontWeight={ 500 } lineHeight="normal" color={ titleColor }>
              { t(ADDRESS_CONTRACT_KEYS.batchPaste.title) }
            </Text>
            <IconButton
              aria-label={ t(ADDRESS_CONTRACT_KEYS.batchPaste.close) }
              icon={ <IconSvg name="cross" boxSize={ 4 } color={ closeIconColor }/> }
              variant="ghost"
              size="xs"
              minW="16px"
              h="16px"
              color={ closeIconColor }
              onClick={ handleClose }
              _hover={{ bg: 'transparent', opacity: 0.8 }}
            />
          </Flex>

          <Textarea
            value={ value }
            onChange={ handleValueChange }
            placeholder={ t(ADDRESS_CONTRACT_KEYS.batchPaste.placeholder) }
            variant="unstyled"
            bgColor={ textareaBg }
            border="1px solid"
            borderColor={ textareaBorder }
            borderRadius="4px"
            fontSize="12px"
            fontWeight={ 500 }
            lineHeight="normal"
            minH="96px"
            h="auto"
            p={ 2 }
            resize="vertical"
            color={ textColor }
            _hover={ textareaStateProps }
            _focus={ textareaStateProps }
            _focusVisible={ textareaStateProps }
            _placeholder={{ color: placeholderColor }}
          />

          { invalidAddresses.length > 0 && (
            <Box
              bg={ errorBg }
              border="1px solid"
              borderColor={ errorBorder }
              borderRadius="4px"
              p={ 2 }
            >
              <Flex alignItems="flex-start" gap={ 1 }>
                <IconSvg name="info-circle" boxSize={ 4 } color={ errorTextColor } flexShrink={ 0 }/>
                <Flex flexDir="column" gap={ 2 }>
                  <Text fontSize="12px" fontWeight={ 500 } color={ errorTextColor } lineHeight="normal">
                    { t(ADDRESS_CONTRACT_KEYS.batchPaste.invalidAddresses) }
                  </Text>
                  { invalidAddresses.map((address, index) => (
                    <Text key={ `${ address }-${ index }` } fontSize="12px" color={ textColor } lineHeight="normal">
                      { address }
                    </Text>
                  )) }
                </Flex>
              </Flex>
            </Box>
          ) }
        </Flex>

        <Box opacity={ canAdd ? 1 : 0.4 } alignSelf="flex-start">
          <Button
            h="24px"
            px="12px"
            fontSize="14px"
            fontWeight={ 500 }
            lineHeight="22px"
            variant="unstyled"
            bg={ styles.brandColor }
            color={ styles.brandButtonText }
            border="1px solid"
            borderColor={ panelBorder }
            borderRadius="4px"
            onClick={ handleAdd }
            isDisabled={ !canAdd }
            _hover={{ bg: styles.brandColor }}
            _disabled={{ opacity: 1, cursor: 'not-allowed' }}
          >
            { t(ADDRESS_CONTRACT_KEYS.actions.add) }
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

export default React.memo(ContractMethodAddressArrayBatchPaste);
